import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

export interface DropdownOption {
  value: string | number
  label: string
}

interface DropdownProps {
  options: DropdownOption[]
  value?: string | number
  onValueChange: (value: string | number) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  direction?: 'down' | 'up'
  triggerClassName?: string
  panelClassName?: string
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onValueChange,
  placeholder,
  className,
  disabled = false,
  direction = 'down',
  triggerClassName,
  panelClassName,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const selectedOption = options.find(opt => opt.value === value)
  const displayText = selectedOption?.label || placeholder || 'Select...'

  const handleSelect = (optValue: string | number) => {
    onValueChange(optValue)
    setOpen(false)
  }

  return (
    <div className={cn("relative", className)} ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "px-3 py-2 rounded-md bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2 w-full justify-between",
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown
          className={cn("size-4 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div
          className={cn(
            "absolute left-0 bg-card border rounded-md shadow-lg z-50 min-w-full max-h-48 overflow-y-auto",
            direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1',
            panelClassName
          )}
          onClick={(e) => e.stopPropagation()}
          role="listbox"
        >
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                "w-full text-left px-4 py-2 text-sm transition-colors",
                value === option.value
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground hover:bg-muted/50"
              )}
              role="option"
              aria-selected={value === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
Dropdown.displayName = "Dropdown"

export { Dropdown }
